import React, { useEffect, useState } from "react";
import { usePWA } from "../../context/PWAContext";
import { useBranding } from "../../context/BrandingContext";
import { Download, X } from "lucide-react";

const STORAGE_VISIT_KEY = "kb_visit_count";
const STORAGE_DISMISS_KEY = "kb_install_dismissed";

/**
 * InstallBanner — subtle bottom strip prompting Install on the second visit.
 * Only renders if (a) the browser fired beforeinstallprompt, (b) the user has
 * visited at least twice, and (c) they haven't dismissed it before.
 */
export default function InstallBanner() {
  const { isInstallable, promptInstall } = usePWA();
  const { branding } = useBranding();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Bump the visit counter on each fresh page-load.
    let visits = 0;
    try {
      visits = parseInt(localStorage.getItem(STORAGE_VISIT_KEY) || "0", 10) || 0;
      localStorage.setItem(STORAGE_VISIT_KEY, String(visits + 1));
    } catch { /* private mode */ }
    const dismissed = (() => {
      try { return localStorage.getItem(STORAGE_DISMISS_KEY) === "1"; }
      catch { return false; }
    })();
    if (visits + 1 >= 2 && !dismissed) {
      // Defer the show by 2s so it doesn't fight the hero animation
      const t = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  if (!visible || !isInstallable) return null;

  const brand = branding?.brand_name || "KalkiBazaar";

  return (
    <div
      className="fixed bottom-3 left-3 right-3 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-40 bg-white border border-[hsl(var(--kb-border))] shadow-2xl px-4 py-3 flex items-center gap-3 animate-in slide-in-from-bottom"
      role="dialog"
      aria-live="polite"
      data-testid="pwa-install-banner"
    >
      <div className="w-10 h-10 rounded-lg bg-[hsl(var(--kb-primary))] text-white flex items-center justify-center shrink-0">
        <Download size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Install {brand}</div>
        <div className="text-xs text-[hsl(var(--kb-muted))] truncate">
          Faster repeat visits, works offline.
        </div>
      </div>
      <button
        onClick={async () => { await promptInstall(); dismiss(); }}
        className="px-4 py-1.5 bg-[hsl(var(--kb-primary))] text-white text-xs uppercase tracking-widest"
        data-testid="pwa-install-accept"
      >
        Install
      </button>
      <button
        onClick={dismiss}
        className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))]"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
